import { useEffect, useMemo, useState } from "react";
import { PageDS, Spacer, TabMenu, ToastContainerDS } from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { AdvancedAnalyticsVehiclesTab } from "./advancedAnalyticsVehiclesTab";
import { HistoryTab } from "./historyTab";
import { MonthlyReportsTab } from "./monthlyReportsTab";
import {
  advancedAnalyticVehiclesByDepartmentAsync,
  monthlyMileageGapReportsListByUserAsync,
  selectAdvancedAnalyticsVehiclesListStatus,
  selectMonthlyReportsListStatus,
} from "../../home/fleet/fleetSlice";
import { selectCurrentDepartment } from "../departments/departmentsSlice";
import { selectManager } from "../../home/homeSlice";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";

export const DepartmentFleet = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const firstPath = location.pathname.split("/")[1];
  const dispatch = useAppDispatch();
  const department = useAppSelector(selectCurrentDepartment);
  const advancedAnalyticVehiclesListStatus = useAppSelector(
    selectAdvancedAnalyticsVehiclesListStatus,
  );
  const monthlyReportsListStatus = useAppSelector(
    selectMonthlyReportsListStatus,
  );
  const manager = useAppSelector(selectManager);
  const params = useParams();
  const [selectedTab, setSelectedTab] = useState<string>(
    params.tab ?? "advanced_analytics",
  );

  type EnvType = "production" | "staging" | "demo" | "development";
  const env = import.meta.env["VITE_TARGET_ENV"] as EnvType;
  const showMonthlyReportsTab = env !== "demo" && env !== "production";

  const tabs = useMemo(() => {
    const base = [
      {
        name: "advanced_analytics",
        title: t("fleet.tab.advanced_analytics"),
      },
      {
        name: "history",
        title: t("fleet.tab.tripsHistory"),
      },
    ];
    if (showMonthlyReportsTab) {
      base.push({
        name: "monthly_reports",
        title: t("fleet.tab.monthly_reports"),
      });
    }
    return base;
  }, [t, showMonthlyReportsTab]);

  useEffect(() => {
    if (!showMonthlyReportsTab && selectedTab === "monthly_reports") {
      setSelectedTab("advanced_analytics");
    }
  }, [showMonthlyReportsTab, selectedTab]);

  useEffect(() => {
    if (selectedTab) {
      navigate(
        `/${firstPath}/fleet/${selectedTab}${
          params.id !== undefined ? `/${params.id}` : ""
        }`,
      );
    }
  }, [firstPath, navigate, params.id, selectedTab]);

  useEffect(() => {
    if (department && !department.hasAdvancedAnalytics) {
      navigate(`/${firstPath}`);
    }
  }, [department, navigate, firstPath]);

  useEffect(() => {
    if (department && advancedAnalyticVehiclesListStatus === "idle") {
      void dispatch(advancedAnalyticVehiclesByDepartmentAsync(department.uuid));
    }
  }, [dispatch, department, advancedAnalyticVehiclesListStatus]);

  useEffect(() => {
    if (
      department &&
      manager &&
      showMonthlyReportsTab &&
      monthlyReportsListStatus === "idle"
    ) {
      void dispatch(monthlyMileageGapReportsListByUserAsync(manager.uuid));
    }
  }, [
    department,
    dispatch,
    manager,
    monthlyReportsListStatus,
    showMonthlyReportsTab,
  ]);

  return (
    <PageDS
      title={t(`fleet.title`)}
      toaster={<ToastContainerDS />}
      topElement={
        <>
          <TabMenu
            tabs={tabs}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
          />
          <Spacer y={0.5} />
        </>
      }
      supportButton={{
        showMail: true,
      }}
    >
      <AdvancedAnalyticsVehiclesTab />
      <HistoryTab />
      <MonthlyReportsTab />
    </PageDS>
  );
};
